/* ═══════════════════════════════════════════════════════════════════════════
   TABLES, CAPACITY AND WHO GETS WHICH ONE

   ── The rule the whole file turns on ──────────────────────────────────────

   **A table is the restaurant's capacity, not the guest's choice.**

   That is the prototype's decision, in its own words, and it is the right one
   for the side of the marketplace that pays: a guest who can see the plan
   takes the window table for two when they are two, and the six-seater sits
   empty at seven on a Saturday. The room sells itself worse than the system
   sells it.

   What the guest chooses is **how many they are**, and **which part of the
   room** — a terrace, the water side, a private room — because that is a
   genuine preference about the evening rather than a lever on the yield. The
   table itself is assigned inside the zone: the smallest one that holds the
   party, and if none does, two neighbours pushed together.

   So: zone and party size in, table number out. The guest never types a table
   number and neither do we.

   ── And the other way in ──────────────────────────────────────────────────

   The code on the table. Scanning it is not "choosing table 14" — it is
   standing at table 14, which the restaurant can see. The code IS the table,
   and that is why check-in needs no picker either. See domain/tablesession.js.
   ═══════════════════════════════════════════════════════════════════════ */

import { PLAN, SEATING, hasPlan } from '../data/tables.js';

export { hasPlan };

/* ── The room, laid out ───────────────────────────────────────────────── */

/**
 * Every table at a business, numbered, in the order the plan lists them.
 *
 * `with` is the neighbour this table may be pushed together with — the next
 * one along, when both are joinable and in the same zone. It is worked out
 * once here rather than searched for at assignment time, because "which table
 * is next to this one" is a fact about the room and not about tonight.
 */
export function tablesAt(businessId) {
  const out = [];
  let n = 1;
  (PLAN[businessId] || []).forEach(([count, seats, zone, join]) => {
    for (let i = 0; i < count; i++, n++) out.push({ n, seats, zone, join: join ? 1 : 0 });
  });
  out.forEach((t, i) => {
    const next = out[i + 1];
    t.with = (t.join && next && next.join && next.zone === t.zone) ? next.n : 0;
  });
  return out;
}

/** The zones a place has, in plan order, with what sits in each. */
export function zonesAt(businessId) {
  const out = [];
  tablesAt(businessId).forEach(t => {
    const had = out.find(z => z.id === t.zone);
    if (had) { had.tables += 1; had.seats += t.seats; had.largest = Math.max(had.largest, t.seats); }
    else out.push({ id: t.zone, name: t.zone, tables: 1, seats: t.seats, largest: t.seats });
  });
  return out;
}

export const seatsAt = businessId =>
  tablesAt(businessId).reduce((a, t) => a + t.seats, 0);

export const tableCount = businessId => tablesAt(businessId).length;

/** The biggest party a place can seat at all, joining included. A party of
    fourteen is told so before it picks a date, not after. */
export function largestParty(businessId) {
  const all = tablesAt(businessId);
  let best = 0;
  all.forEach(t => {
    best = Math.max(best, t.seats);
    if (t.with) {
      const nb = all.find(x => x.n === t.with);
      if (nb) best = Math.max(best, t.seats + nb.seats);
    }
  });
  return best;
}

/* ── When the tables turn ─────────────────────────────────────────────── */

const toMin = t => {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + (m || 0);
};
const toWords = n =>
  String(Math.floor(n / 60)).padStart(2, '0') + ':' + String(n % 60).padStart(2, '0');

/** The seating rules for a place, or null where it keeps none. */
export const seatingAt = businessId => SEATING[businessId] || null;

/**
 * The times a table can be reserved for.
 *
 * NOT the opening hours. A kitchen open to eleven with a 165-minute turn
 * stops seating at half past eight, and offering ten o'clock because the door
 * is open is how a table gets promised that cannot be given.
 */
export function sittings(businessId) {
  const s = SEATING[businessId];
  if (!s) return [];
  if (s.mode === 'faste') return [...s.slots];
  const out = [];
  for (let t = toMin(s.from); t <= toMin(s.last); t += 30) out.push(toWords(t));
  return out;
}

/** How a sitting is described where it is offered. */
export function sittingWords(businessId) {
  const s = SEATING[businessId];
  if (!s) return '';
  return s.mode === 'faste'
    ? `Sittings at ${s.slots.join(' and ')}. A table is yours for `
      + `${s.turn} minutes, with ${s.buffer} to clear it between.`
    : `Tables turn over from ${s.from}, last seating ${s.last}. `
      + `Counted at ${s.turn} minutes a table.`;
}

/** How long the table is held, so the diary can draw a block of the right
    length rather than guessing an hour. */
export const turnMinutes = businessId => SEATING[businessId]?.turn || 90;

/* ── Assignment ───────────────────────────────────────────────────────── */

/**
 * Which tables are gone at this sitting.
 * @param {object[]} booked  `[{ tables: number[] }]` — what is already taken
 */
export const takenTables = (booked = []) =>
  booked.reduce((a, r) => a.concat(r.tables || []), []);

/**
 * Give this party a table.
 *
 * The smallest free table that holds them — a four does not go to a two while
 * there is a two free, because the four is what the next party of four needs.
 * Failing that, two joinable neighbours pushed together. Failing that, null,
 * and the screen says the sitting is full rather than offering a table that
 * is not there.
 *
 * `zone` narrows it to one part of the room, which is the only thing the
 * guest gets to choose — see the note at the top of this file.
 */
export function assign(businessId, { guests = 2, booked = [], zone = null } = {}) {
  const party = Math.max(1, Math.round(Number(guests) || 1));
  const gone = takenTables(booked);
  const all = tablesAt(businessId);
  const free = all.filter(t => !gone.includes(t.n) && (!zone || t.zone === zone));

  const fits = free.filter(t => t.seats >= party).sort((a, b) => a.seats - b.seats || a.n - b.n);
  if (fits.length) {
    const t = fits[0];
    return { tables: [t.n], seats: t.seats, zone: t.zone, joined: false };
  }

  /* Pushed together. The neighbour has to be free as well, which is why this
     is checked against `free` rather than against the plan. */
  for (const t of free) {
    if (!t.with) continue;
    const nb = free.find(x => x.n === t.with);
    if (nb && t.seats + nb.seats >= party) {
      return { tables: [t.n, nb.n], seats: t.seats + nb.seats, zone: t.zone, joined: true };
    }
  }
  return null;
}

/**
 * How many more parties this size could still be seated — for "3 tables left"
 * rather than a bare yes or no.
 *
 * Counted as tables that cannot be used twice. The obvious version adds one
 * for every joinable pair, which counts tables 9+10 and 10+11 as two ways to
 * seat two parties — and table 10 cannot do both. A place with ten
 * neighbouring fours then claims nine pairs where it has five, and tells the
 * fourth caller there is room when there is not.
 *
 * So: singles first, then pairs made only from what the singles did not use,
 * and no table taken twice.
 */
export function freeFor(businessId, { guests = 2, booked = [], zone = null } = {}) {
  const party = Math.max(1, Math.round(Number(guests) || 1));
  const gone = takenTables(booked);
  const free = tablesAt(businessId)
    .filter(t => !gone.includes(t.n) && (!zone || t.zone === zone));

  const used = new Set();
  let n = 0;
  free.forEach(t => { if (t.seats >= party) { used.add(t.n); n += 1; } });
  free.forEach(t => {
    if (used.has(t.n) || !t.with) return;
    const nb = free.find(x => x.n === t.with);
    if (!nb || used.has(nb.n)) return;
    if (t.seats + nb.seats < party) return;
    used.add(t.n); used.add(nb.n); n += 1;
  });
  return n;
}

/** The line printed on the confirmation. Two tables say so — a guest who
    booked "table 7" and finds 7 and 8 pushed together has been surprised. */
export function tableWords(a) {
  if (!a) return '';
  return a.joined
    ? `Tables ${a.tables.join(' + ')} · ${a.zone.toLowerCase()} · pushed together, ${a.seats} seats`
    : `Table ${a.tables[0]} · ${a.zone.toLowerCase()} · ${a.seats} seats`;
}

/**
 * Why a party cannot be seated, in a sentence worth reading.
 *
 * The difference matters: "we have nothing that big at all" is a different
 * problem from "that sitting is full", and telling somebody to try another
 * evening when no evening would work wastes their time twice.
 */
export function refusal(businessId, guests, sitting) {
  const most = largestParty(businessId);
  if (guests > most) {
    return `The largest table here seats ${most}, even with two pushed together. `
         + 'For a bigger party, ask them directly — they may open a room.';
  }
  return `No table left for ${guests} at ${sitting}. Another sitting may have one.`;
}
